// ═══════════════════════════════════════════════════════════════════════════
// appCheckDiagnostico — por qué App Check no se inicializó, dicho en voz alta
// ═══════════════════════════════════════════════════════════════════════════
//
// EL PROBLEMA (report oz7FcUdw1n1jj6ruTjUO)
// ─────────────────────────────────────────
// `firebase.js` inicializa App Check sólo `if (esNavegador && RECAPTCHA_SITE_KEY)`.
// Si `VITE_RECAPTCHA_SITE_KEY` no entró al build, ese `if` no entra y **no pasa
// nada**: ni un log, ni un warn — el `console.warn` del catch de al lado sólo
// dispara si `initializeAppCheck` TIRA, y acá nunca se lo llama.
//
// El bundle queda sin App Check. Los callables que declaran
// `enforceAppCheck: true` rechazan con 401 **antes** de llegar al handler, así
// que la app deployada no funciona y la única evidencia es el 401 en la red.
// Ya pasó una vez con el gemelo de staging (bug #302, 12-may-2026,
// `docs/CHANGELOG.md`): la causa tardó en encontrarse porque el sistema no la
// decía. El eje que faltaba no era el candado — era el diagnóstico.
//
// QUÉ HACE ESTE ARCHIVO Y QUÉ NO
// ──────────────────────────────
// Clasifica, y nada más: función pura, sin side effects, sin leer el entorno
// (lo recibe). No emite el mensaje —eso es del consumidor— y **no cambia el
// comportamiento de App Check**: la condición del `if` de `firebase.js` sigue
// siendo exactamente la misma.
//
// POR QUÉ NO TIRA
// ───────────────
// Fail-closed ya se cumple sin nosotros: la key ausente no ABRE nada — el
// enforcement vive en el backend (`enforceAppCheck: true` en `functions/`) y
// sigue rechazando. Lo que falta es la otra mitad del precepto: la causa real
// trazable. Y un `throw` en un módulo que se importa en el boot es pantalla en
// blanco garantizada — el mismo argumento que `cotaDeLectura.js:31-36` deja
// escrito: cambiar un problema de configuración por uno de disponibilidad es
// peor. Se grita, no se rompe.
//
// ⚠️ POR QUÉ `esDev` Y NO EL HOSTNAME NI EL projectId
// ───────────────────────────────────────────────────
// El modo de falla es de BUILD TIME (una var que no está cuando corre `vite
// build`), y `import.meta.env.DEV` es una constante de build time: el eje
// matchea la falla. El hostname es runtime y no ve un build local roto.
// El `projectId` NO sirve: en dev local sin override el default es prod
// (`firebase.js:88-91`), así que gritaría en cada `npm run dev` sin key.
//
// Y OJO CON LA TRAMPA CONOCIDA, QUE ACÁ JUEGA A FAVOR: `npm run build` corre
// sin `--mode`, así que en **staging `import.meta.env.DEV` es `false`**
// (`useDashboardData.js:133-137`, hallazgo del adversarial `4006fd9`). En
// aquel caso ese hecho APAGABA un candado; acá PRENDE la alarma, y está bien
// que la prenda: un bundle de staging sin key también está roto. No lo
// "arregles" al revés.
// ═══════════════════════════════════════════════════════════════════════════

/** App Check no aplica: no hay navegador (Cloud Functions, shim del CI). */
export const OMITIDO_HEADLESS = 'omitido-headless'
/** Hay site key: App Check se inicializa. Camino feliz, no se dice nada. */
export const OK = 'ok'
/** Dev local sin key: modo soportado y documentado (`firebase.js:126`). */
export const DEV_SIN_KEY = 'dev-sin-key'
/** Bundle DEPLOYADO sin key: esto es el bug. */
export const DEPLOY_SIN_KEY = 'deploy-sin-key'

/**
 * Clasifica por qué App Check quedó (o no) inicializado.
 *
 * `siteKey` se evalúa con `Boolean(...)` **crudo, sin trim**, a propósito: es
 * el mismo predicado que usa el `if` de `firebase.js`. Si acá normalizáramos,
 * un `'   '` daría `deploy-sin-key` mientras App Check SÍ intenta arrancar —
 * el diagnóstico contradiría a la rama que dice describir, y habría dos
 * fuentes de verdad sobre el mismo predicado.
 *
 * @param {object} entrada
 * @param {boolean} entrada.esNavegador  navegador de verdad (window + location)
 * @param {string|undefined|null} entrada.siteKey  `import.meta.env.VITE_RECAPTCHA_SITE_KEY`
 * @param {boolean} entrada.esDev  `import.meta.env.DEV`
 * @returns {{nivel: string, mensaje: string|null}}
 */
export function diagnosticarAppCheck({ esNavegador, siteKey, esDev } = {}) {
  if (!esNavegador) {
    return { nivel: OMITIDO_HEADLESS, mensaje: null }
  }
  if (Boolean(siteKey)) {
    return { nivel: OK, mensaje: null }
  }
  if (esDev) {
    return {
      nivel: DEV_SIN_KEY,
      mensaje:
        'App Check no se inicializó: no hay VITE_RECAPTCHA_SITE_KEY en el entorno. ' +
        'En dev local es lo esperado — los callables con enforceAppCheck van a rechazar ' +
        'con 401 hasta que definas la key en tu .env.local.',
    }
  }
  return {
    nivel: DEPLOY_SIN_KEY,
    // Sin números: el conteo de callables con `enforceAppCheck` envejece mal y
    // ya mintió por 62 en este mismo report (`docs/INFRAESTRUCTURA.md:276`
    // dejó escrito que no se cita de memoria, se cuenta). Y sin secretos: ni
    // la key, ni el debug token, ni un dump de `import.meta.env`.
    mensaje:
      'App Check NO está activo en un build deployado: falta VITE_RECAPTCHA_SITE_KEY ' +
      'en el paso de build del workflow que generó este bundle. Los callables que ' +
      'declaran enforceAppCheck rechazan con 401 antes de llegar al handler, así que ' +
      'la app va a parecer rota sin que el error diga por qué. Se arregla en el ' +
      'workflow, no en el navegador.',
  }
}
